/**
 * route_key normalization — the ONE definition, imported by BOTH the checklist
 * generator (server-side spec ingest) and the widget bundle.
 *
 * A finding and a checklist must agree on what "the same page" means, so this
 * must never be re-inlined on either side. Table-driven tests in
 * `route-key.test.ts`.
 *
 * This file is bundled into the browser widget: it must stay dependency-free
 * (no zod, no server imports).
 *
 * Rules:
 *   1. Take the pathname plus SUB-VIEW query params (per-project, default
 *      `view` + `tab`); every other query param and the hash are dropped.
 *   2. Strip a leading locale segment (per-project locale list).
 *   3. Replace id-shaped segments (UUID, numeric, cuid-ish, share tokens)
 *      with `[id]` so `/invoices/123e…` and `/invoices/456` collapse.
 *   4. Drop the trailing slash; the root is `/`.
 *   5. A sub-view param whose value IS the page's default collapses into the
 *      bare pathname (`/journey?view=dashboard` ≡ `/journey`), so the default
 *      view and a bare visit are the same surface.
 *
 * Two normalizers, because "the surface a tester is ON" and "the bucket a
 * scenario is pinned TO" need different treatment of the default sub-view:
 *
 *   - `toRouteKey`   — URLs. Collapses the default (rule 5): a bare visit and
 *                      the explicit default value ARE the same screen.
 *   - `toSurfaceKey` — `**Surface:**` declarations. KEEPS the default, so an
 *                      author can pin a scenario to the default view
 *                      specifically instead of to the whole page.
 *
 * The server folds the default-view bucket back into whichever request is
 * actually showing that view (`server/checklist-lookup.ts`).
 *
 * Multi-tenant change from the single-tenant original: locales, sub-view params
 * and default sub-views are no longer hardcoded to one product — they are
 * per-project configuration (`qa_projects.route_config`), passed in at every
 * call site. The widget receives them from `GET /api/qa/config`.
 */

/**
 * The every-page bucket. NOT a route: a scenario filed here shows on every
 * surface, which is why it is spelled with a leading underscore — no URL
 * pathname can collide with it.
 *
 * It has two populations, and telling them apart is the whole point of
 * `isGlobalSurface` below:
 *
 *  - scenarios the mapper could not place (flagged `unmapped`, listed for
 *    review — the no-silent-drop rule), and
 *  - scenarios an author DECLARED global with `**Surface:** _global`.
 *
 * Before the declaration existed, "global" was only expressible as the ABSENCE
 * of evidence, so every improvement in mapper evidence silently converted an
 * author's intent into a placement: the first `routePatterns`-enabled sync of a
 * real tenant moved two deliberately-app-wide spec files onto a catch-all page
 * and emptied `_global` outright.
 */
export const GLOBAL_ROUTE_KEY = "_global";

/**
 * Is this `**Surface:**` token the explicit global declaration?
 *
 * Case-insensitive so `_GLOBAL` in a spec is not a silent miss, and it must be
 * checked BEFORE `toSurfaceKey`, which would otherwise normalize the token into
 * the perfectly valid — and completely wrong — route key `/_global`.
 */
export function isGlobalSurface(token: string): boolean {
  return token.trim().toLowerCase() === GLOBAL_ROUTE_KEY;
}

/**
 * Per-project normalization inputs. Stored on `qa_projects.route_config` and
 * served to the widget; every function here takes it explicitly so the two
 * sides can never diverge on a default.
 */
export interface RouteKeyConfig {
  /** Leading path segments to strip (i18n prefixes), e.g. `["en","sv"]`. */
  locales: readonly string[];
  /** Query params that denote a major in-page surface. */
  subviewParams: readonly string[];
  /** pathname → { param: defaultValue } — the value that means "bare visit". */
  defaultSubviews: Readonly<Record<string, Readonly<Record<string, string>>>>;
  /**
   * The app's actual route table, in its own notation:
   * `["/", "/wiki/[slug]", "/invoices/[id]", "/[...slug]"]`.
   *
   * Supplied by `qadeck-sync`, which reads it off the page files. When present
   * it REPLACES the id-shape guess below, and that is the whole point: a
   * human-readable slug is indistinguishable from a static segment by shape
   * alone, so `/wiki/getting-started` used to key to itself and render an empty
   * panel while its checklist sat under `/wiki/[id]`. Matching the real table
   * removes the guess in both directions — a static segment that happens to
   * look like an id also stops collapsing.
   *
   * Empty for an upload-only project (no repo to read), which falls back to the
   * shape heuristic.
   */
  routePatterns?: readonly string[];
}

/** Neutral defaults for a project that has configured nothing yet. */
export const DEFAULT_ROUTE_KEY_CONFIG: RouteKeyConfig = {
  locales: [],
  subviewParams: ["view", "tab"],
  defaultSubviews: {},
  routePatterns: [],
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMERIC_RE = /^\d+$/;
/** Opaque ids: long mixed-case/alphanumeric blobs (cuid, nanoid, share tokens). */
const OPAQUE_ID_RE = /^(?=.*\d)[A-Za-z0-9_-]{16,}$/;

function isIdSegment(segment: string): boolean {
  return (
    UUID_RE.test(segment) ||
    NUMERIC_RE.test(segment) ||
    OPAQUE_ID_RE.test(segment)
  );
}

/** `[id]`, `[slug]`, `[...path]`, `[[...path]]` — a placeholder, not a literal. */
function isDynamicSegment(segment: string): boolean {
  return segment.startsWith("[") && segment.endsWith("]");
}

function isCatchAllSegment(segment: string): boolean {
  return segment.startsWith("[...") || segment.startsWith("[[...");
}

/**
 * Match a visited path against the app's route table.
 *
 * Precedence follows the router's own: a static segment beats a dynamic one,
 * and a dynamic one beats a catch-all — so `/wiki/new` wins over `/wiki/[slug]`
 * if the app defines both. Every dynamic segment collapses to `[id]` in the
 * result, matching the key the checklist pipeline files scenarios under; a
 * catch-all absorbs all remaining segments into that one `[id]`.
 *
 * Returns null when nothing matches, which is not a failure — an unknown path
 * (a 404, a route added since the last sync) falls through to the shape guess.
 */
function matchRoutePattern(
  segments: readonly string[],
  patterns: readonly string[],
): string | null {
  let best: string[] | null = null;
  let bestScore = -1;

  for (const pattern of patterns) {
    const parts = pattern.split("/").filter(Boolean);
    const catchAll = parts.findIndex(isCatchAllSegment);

    let matches: boolean;
    if (catchAll === -1) {
      matches =
        parts.length === segments.length &&
        parts.every((p, i) => isDynamicSegment(p) || p === segments[i]);
    } else {
      // A catch-all needs at least one segment to absorb; an OPTIONAL one
      // (`[[...path]]`) also matches the bare prefix.
      const required = parts[catchAll].startsWith("[[")
        ? catchAll
        : catchAll + 1;
      matches =
        segments.length >= required &&
        parts
          .slice(0, catchAll)
          .every((p, i) => isDynamicSegment(p) || p === segments[i]);
    }
    if (!matches) continue;

    // Static segments are the specificity signal; the catch-all penalty breaks
    // ties between `/a/[x]` and `/a/[...x]` in favour of the narrower one.
    const score =
      parts.filter((p) => !isDynamicSegment(p)).length * 10 +
      (catchAll === -1 ? 5 : 0);
    if (score > bestScore) {
      bestScore = score;
      best = parts;
    }
  }

  if (!best) return null;
  const normalized = best.map((p) => (isDynamicSegment(p) ? "[id]" : p));
  return normalized.length ? `/${normalized.join("/")}` : "/";
}

/**
 * Normalize a URL (absolute or path-only) into a route key.
 *
 * With `{ locales: ["sv","en"], defaultSubviews: { "/documents": { tab: "invoices" } } }`:
 *
 * `/sv/documents?tab=estimates`            → `/documents?tab=estimates`
 * `/sv/documents?tab=invoices`             → `/documents` (default tab)
 * `/en/journey?view=progress&utm=x`        → `/journey?view=progress`
 * `/en/invoices/123e4567-e89b-…`           → `/invoices/[id]`
 * `https://staging.example/en/journey#x`   → `/journey`
 * `/`, `/en`, `/en/`                       → `/`
 */
export function toRouteKey(
  urlOrPath: string,
  config: RouteKeyConfig = DEFAULT_ROUTE_KEY_CONFIG,
): string {
  return normalize(urlOrPath, config, true);
}

/**
 * Normalize a `**Surface:**` DECLARATION into a bundle key.
 *
 * Identical to `toRouteKey` except that a default sub-view value is KEPT, so a
 * scenario can be pinned to the page's default view specifically:
 *
 * `/journey?view=dashboard`    → `/journey?view=dashboard`  (stays distinct)
 * `/en/documents?tab=invoices` → `/documents?tab=invoices`
 * `/journey?view=progress`     → `/journey?view=progress`   (same as toRouteKey)
 */
export function toSurfaceKey(
  urlOrPath: string,
  config: RouteKeyConfig = DEFAULT_ROUTE_KEY_CONFIG,
): string {
  return normalize(urlOrPath, config, false);
}

/**
 * The explicit default-view key for a pathname, or `null` when the page has no
 * declared default: `/journey` → `/journey?view=dashboard`, `/pricing` → null.
 */
export function defaultSurfaceKey(
  pathname: string,
  config: RouteKeyConfig = DEFAULT_ROUTE_KEY_CONFIG,
): string | null {
  const defaults = config.defaultSubviews[pathname];
  if (!defaults) return null;
  const kept: string[] = [];
  for (const name of config.subviewParams) {
    const value = defaults[name]?.trim().toLowerCase();
    if (value) kept.push(`${name}=${value}`);
  }
  return kept.length ? `${pathname}?${kept.join("&")}` : null;
}

function normalize(
  urlOrPath: string,
  config: RouteKeyConfig,
  collapseDefaults: boolean,
): string {
  let pathname = urlOrPath;
  let query = "";

  // Absolute URL → pathname. `URL` is available in both the browser and Node.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(urlOrPath)) {
    try {
      const parsed = new URL(urlOrPath);
      pathname = parsed.pathname;
      query = parsed.search;
    } catch {
      // fall through with the raw string
    }
  }

  const cut = pathname.search(/[?#]/);
  if (cut !== -1) {
    if (!query) {
      const hashCut = pathname.indexOf("#");
      const tail = pathname.slice(cut, hashCut === -1 ? undefined : hashCut);
      if (tail.startsWith("?")) query = tail;
    }
    pathname = pathname.slice(0, cut);
  }

  const segments = pathname.split("/").filter(Boolean);

  const locales = config.locales.map((l) => l.toLowerCase());
  if (segments.length && locales.includes(segments[0].toLowerCase())) {
    segments.shift();
  }

  // The app's real route table first; the id-SHAPE guess only when there is no
  // table, or the path is not in it. Shape alone cannot tell a slug
  // (`/wiki/getting-started`) from a static page, which is how a checklist
  // pinned to `/wiki/[id]` ended up unreachable from the page it describes.
  const patterns = config.routePatterns ?? [];
  const matched = patterns.length
    ? matchRoutePattern(segments, patterns)
    : null;
  const path =
    matched ??
    (segments.length
      ? `/${segments.map((s) => (isIdSegment(s) ? "[id]" : s)).join("/")}`
      : "/");

  // Keep only sub-view params, in the project's declared order; drop a value
  // that is the page's declared default (bare visit ≡ explicit default view).
  const params = new URLSearchParams(query);
  const kept: string[] = [];
  for (const name of config.subviewParams) {
    const value = params.get(name)?.trim().toLowerCase();
    if (!value) continue;
    if (collapseDefaults && config.defaultSubviews[path]?.[name] === value) {
      continue;
    }
    kept.push(`${name}=${value}`);
  }

  return kept.length ? `${path}?${kept.join("&")}` : path;
}

/** The pathname part of a route key: `/journey?view=progress` → `/journey`. */
export function routeKeyPathname(routeKey: string): string {
  const cut = routeKey.indexOf("?");
  return cut === -1 ? routeKey : routeKey.slice(0, cut);
}

/**
 * Is this token plausibly a route reference a spec author wrote in a step?
 * Used by the v1 route mapper, which — with no repo access — can only learn a
 * scenario's surface from an explicit `**Surface:**` or a path spelled out in
 * its own text.
 *
 * Deliberately strict. A bare word is never a route, `//host` is a
 * protocol-relative URL rather than a path, and the bare root `/` is rejected
 * outright: it appears in ordinary prose far too often to be evidence, and
 * mapping a scenario to the home page on that basis would be worse than
 * leaving it in `_global`.
 */
export function looksLikeRoutePath(token: string): boolean {
  return /^\/(?![/\s])[A-Za-z0-9\-_/[\]().?=&]+$/.test(token);
}
